import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import thLarge from '@iconify/icons-fa-solid/th-large';
import dice from '@iconify/icons-fa-solid/dice';
import fire from '@iconify/icons-fa-solid/fire';
import rocket from '@iconify/icons-fa-solid/rocket';
import globe from '@iconify/icons-fa-solid/globe';
import SidebarMenuItem from './SidebarMenuItem';
import AccordionMenuItem from './AccordionMenuItem';
import MenuCard from './MenuCard';
import TokenSection from './TokenSection';
import TopStream from './TopStream';
import LanguageSwitcher from '@/components/common/LanguageSwitcher/LanguageSwitcher';
import LanguageModal from '@/components/common/LanguageSwitcher/LanguageModal';
import Button from '@/components/common/Button/Button';
import TextGold from '@/components/common/TextGold/TextGold';
import SportsExchangeIcon from '@/components/icons/SportsExchangeIcon';
import { LANGUAGES } from '@/i18n';
import { storage } from '@/utils';
import styles from './Sidebar.module.css';
import menuItemStyles from './SidebarMenuItem.module.css';
import bonusCardStyles from './WelcomeBonusCard.module.css';

/* ── Sidebar icons: golden-icon set (public/assets/images/golden-icon) + fallback bottomicons ── */
const GOLDEN_ICONS = {
  sports: '/assets/images/newicons/sports.svg',
  promotion: '/assets/images/newicons/promotion.svg',
  bonus: '/assets/images/newicons/bouns-rakeback.svg',
  giftCard: '/assets/images/newicons/giftcard.svg',
  vip: '/assets/images/newicons/vip-club.svg',
  rewards: '/assets/images/newicons/rewards.svg',
  affiliate: '/assets/images/newicons/affiliate.svg',
  provablyFair: '/assets/images/newicons/provably.svg',
  download: '/assets/images/newicons/download.svg',
};

const SIDEBAR_ICONS = {
  casino: '/assets/images/newicons/casino.svg',
  games: GOLDEN_ICONS.sports,
  trade: '/assets/images/newicons/p2p.svg',
  rewards: GOLDEN_ICONS.rewards,
  home: '/assets/images/bottomicons/home.webp',
  menu: '/assets/images/newicons/menu.svg',
};

const ImgIcon = ({ src, alt = '' }) => (
  <img src={src} alt={alt} loading="eager" />
);

/**
 * MenuContent Component – CRYPTMET Rollbit-style sectioned sidebar
 * Token → Main Nav → NEW ON CRYPTMET → PVP → MORE
 */
const MenuContent = ({ isCollapsed, onItemClick }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showLangModal, setShowLangModal] = useState(false);

  const currentLang = LANGUAGES.find((l) => l.code === i18n.language) || LANGUAGES[0];

  useEffect(() => {
    const logged =
      storage.getKey('logged') === true ||
      (typeof localStorage !== 'undefined' && localStorage.getItem('logged') === 'true');
    setIsLoggedIn(logged);
  }, []);

  // Casino sub-items
  const casinoSubItems = [
    { name: t('casino.allGames'), path: '/casino', icon: <Icon icon={thLarge} width={16} height={16} /> },
    { name: t('casino.liveCasino'), path: '/casino/live-casino', icon: <Icon icon={dice} width={16} height={16} /> },
    { name: t('casino.slots'), path: '/casino/popular-slots', icon: <Icon icon={fire} width={16} height={16} /> },
    { name: t('casino.crashGames'), path: '/casino/crash-games', icon: <Icon icon={rocket} width={16} height={16} /> },
  ];

  // Sports sub-items (matching GAMES config from sportsConfig.js)
  const sportsSubItems = [
    { name: t('sports.cricket'), path: '/sports/in-play/cricket', icon: '/assets/images/sports-icons/cricket.svg' },
    { name: t('sports.football'), path: '/sports/in-play/football', icon: '/assets/images/sports-icons/soccer.svg' },
    { name: t('sports.tennis'), path: '/sports/in-play/tennis', icon: '/assets/images/sports-icons/tennis.svg' },
    { name: t('sports.basketball'), path: '/sports/in-play/basketball', icon: '/assets/images/sports-icons/basketball.svg' },
    { name: 'Baseball', path: '/sports/in-play/baseball', icon: '/assets/images/sports-icons/baseball.svg' },
    { name: 'MMA', path: '/sports/in-play/mma', icon: '/assets/images/sports-icons/mma.svg' },
    { name: 'Rugby Union', path: '/sports/in-play/rugby-union', icon: '/assets/images/sports-icons/rugby-union.svg' },
    { name: 'Ice Hockey', path: '/sports/in-play/ice-hockey', icon: '/assets/images/sports-icons/ice-hockey.svg' },
    { name: 'Table Tennis', path: '/sports/in-play/table-tennis', icon: '/assets/images/sports-icons/table-tennis.svg' },
  ];

  // Live jackpot counter (simulated)
  const [jackpotAmount, setJackpotAmount] = useState(156200);
  const [jackpotPlayers, setJackpotPlayers] = useState(18);
  useEffect(() => {
    const interval = setInterval(() => {
      setJackpotAmount(prev => prev + Math.floor(Math.random() * 50 + 5));
      setJackpotPlayers(Math.floor(Math.random() * 15 + 10));
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // Daily race countdown
  const [raceTimer, setRaceTimer] = useState('');
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const endOfDay = new Date(now);
      endOfDay.setHours(23, 59, 59, 999);
      const diff = endOfDay - now;
      const h = String(Math.floor(diff / 3600000)).padStart(2, '0');
      const m = String(Math.floor((diff % 3600000) / 60000)).padStart(2, '0');
      const s = String(Math.floor((diff % 60000) / 1000)).padStart(2, '0');
      setRaceTimer(`${h}:${m}:${s}`);
    };
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, []);

  const formatJackpot = (n) => {
    if (n >= 1000) return `$${(n / 1000).toFixed(1)}K`;
    return `$${n}`;
  };

  // Section header
  const SectionLabel = ({ label }) => {
    if (isCollapsed) return <div className={styles.sectionDividerCollapsed} />;
    return (
      <div className={styles.sectionLabel}>
        <span>{label}</span>
      </div>
    );
  };

  // Badge (right-side info on menu items)
  const Badge = ({ children, variant = 'default' }) => (
    <span className={`${styles.badge} ${variant === 'live' ? styles.badgeLive : ''} ${variant === 'timer' ? styles.badgeTimer : ''}`}>
      {children}
    </span>
  );

  return (
    <>
      {/* ═══════ Token Section ═══════ */}
      {/* <div className={styles.tokenWrapper}>
        <TokenSection isCollapsed={isCollapsed} />
      </div> */}

      {/* ═══════ Top Stream Strips ═══════ */}
      <TopStream isCollapsed={isCollapsed} onItemClick={onItemClick} />

      {/* ═══════ Main Navigation ═══════ */}
      <nav className={styles.nav}>
        {/* Casino */}
        <AccordionMenuItem
          icon={<ImgIcon src={SIDEBAR_ICONS.casino} alt="Casino" />}
          label={t('nav.casino')}
          path="/casino"
          subItems={casinoSubItems}
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
          onItemClick={onItemClick}
        />

        {/* Sports */}
        <AccordionMenuItem
          icon={<ImgIcon src={SIDEBAR_ICONS.games} alt="Sports" />}
          label={t('nav.sports')}
          path="/sports/popular"
          subItems={sportsSubItems}
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
          onItemClick={onItemClick}
        />

        {/* Sports Bet (Slotegrator/BetBy) */}
        <SidebarMenuItem
          icon={<ImgIcon src="/assets/images/newicons/esports.svg" alt="Sports Bet" />}
          label={t('nav.sportsBet', 'Sports Bet')}
          path="/sports-bet"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* P2P Trade — only when logged in */}
        {isLoggedIn && (
          <SidebarMenuItem
            icon={<ImgIcon src={SIDEBAR_ICONS.trade} alt="P2P Trade" />}
            label={t('nav.trade')}
            path="/trade"
            isCollapsed={isCollapsed}
            color="var(--color-text-primary)"
          />
        )}

        {/* Redeem & Rakeback */}
        <SidebarMenuItem
          icon={<ImgIcon src={GOLDEN_ICONS.bonus} alt="Redeem & Rakeback" />}
          label={t('bonus.rakeback')}
          path="/bonus-page"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* Jackpot with live amount + player count */}
        {/* <SidebarMenuItem
          icon={<ImgIcon src={SIDEBAR_ICONS.casino} alt="Jackpot" />}
          label={`Jackpot · ${formatJackpot(jackpotAmount)}`}
          path="/jackpot"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        >
          {!isCollapsed && <Badge variant="live">{jackpotPlayers}</Badge>}
        </SidebarMenuItem> */}

        {/* Promotions */}
        <SidebarMenuItem
          icon={<ImgIcon src={GOLDEN_ICONS.promotion} alt="Promotions" />}
          label={t('nav.promotions', 'Promotions')}
          path="/promotions"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* Gift Cards */}
        <SidebarMenuItem
          icon={<ImgIcon src={GOLDEN_ICONS.giftCard} alt="Gift Cards" />}
          label={t('nav.giftCards')}
          path="/gift-cards"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* ═══════ MORE ═══════ */}
        <SectionLabel label={t('common.more').toUpperCase()} />

        {/* VIP Club */}
        <SidebarMenuItem
          icon={<ImgIcon src={GOLDEN_ICONS.vip} alt="VIP Club" />}
          label={t('nav.vipClub')}
          path="/vip-club"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
          isVip
        />

        {/* Affiliate */}
        <SidebarMenuItem
          icon={<ImgIcon src={GOLDEN_ICONS.affiliate} alt="Affiliate" />}
          label={t('nav.affiliate')}
          path="/affiliate"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* Daily Race with timer */}
        {/* <SidebarMenuItem
          icon={<Trophy size={20} style={{ fill: 'none', stroke: 'currentColor' }} />}
          label="$25K Daily Race"
          path="/daily-race"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        >
          {!isCollapsed && <Badge variant="timer">{raceTimer}</Badge>}
        </SidebarMenuItem> */}

        {/* Provably Fair */}
        <SidebarMenuItem
          icon={<ImgIcon src={GOLDEN_ICONS.provablyFair} alt="Provably Fair" />}
          label={t('nav.provablyFair')}
          path="/provably-fair"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
        />

        {/* Language Switcher */}
        <SidebarMenuItem
          icon={<Icon icon={globe} width={20} height={20} style={{ color: '#D4AF37' }} />}
          label={isCollapsed ? '' : `${currentLang.flag} ${currentLang.nativeName}`}
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
          onClick={() => setShowLangModal(true)}
        />

        {/* Install – small button below Provably Fair */}
        <SidebarMenuItem
          icon={<ImgIcon src={GOLDEN_ICONS.download} alt="Install" />}
          label={t('nav.install')}
          path="/install"
          isCollapsed={isCollapsed}
          color="var(--color-text-primary)"
          className={menuItemStyles.install}
        />

        {/* Welcome Bonus Card – below Provably Fair */}
        <div className={`${bonusCardStyles.wrapper} ${isCollapsed ? bonusCardStyles.collapsed : ''}`}>
          <div className={bonusCardStyles.card}>
            <img
              src="/assets/images/sidebar/bonus.webp"
              alt="Bonus"
              className={bonusCardStyles.cardImage}
              loading="lazy"
              width={232}
              height={148}
            />
            <div className={bonusCardStyles.overlay} aria-hidden />
            <div className={bonusCardStyles.content}>
              <div className={bonusCardStyles.textBlock}>
                <TextGold as="h2" className={bonusCardStyles.title}>
                  {t('home.welcomeBonus')}
                </TextGold>
                <p className={bonusCardStyles.description}>{t('common.deposit')}.</p>
              </div>
              <div className={bonusCardStyles.buttonWrap}>
                <Button
                  type="button"
                  variant="deposit"
                  className={bonusCardStyles.depositButton}
                  onClick={() => {
                    onItemClick?.();
                    if (isLoggedIn) {
                      navigate('/wallet?mode=deposit');
                    } else {
                      navigate('/login?modalType=auth-signin');
                    }
                  }}
                >
                  {t('common.deposit')}
                </Button>
                <div className={bonusCardStyles.glow} aria-hidden />
              </div>
            </div>
          </div>
        </div>
      </nav>
      <LanguageModal isOpen={showLangModal} onClose={() => setShowLangModal(false)} />
    </>
  );
};

export default MenuContent;
